import axios, { isAxiosError } from 'axios';

interface TicketRequester {
  email: string;
  name: string;
}

interface CreateTicketParams {
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high';
  parent_organization_id: number;
  secondary_organization_id?: number;
  chat_session_id?: string;
  topic_id?: number;
  requester?: TicketRequester;
}

interface TicketResponse {
  id: number;
  subject: string;
  description: string;
  priority: string;
  status: string;
  ticket_number: string;
  parent_organization_id: number;
  secondary_organization_id?: number;
  created_by_id?: number;
  assigned_to_id?: number;
  requester?: {
    id: number;
    email: string;
    name: string;
    created_at: string;
    updated_at: string;
  };
  closed_at?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  topic: {
    id: number;
    name: string;
  };
}

interface CreateTicketResponse {
  ticket: TicketResponse;
}

interface CreateTicketError {
  errors: string[];
}

/**
 * Creates a support ticket via the public tasks API.
 * 
 * @param endpoint - Base URL for the writable API
 * @param ticketData - Ticket information including subject, description, organization IDs, and requester details
 * @param recaptcha_token - Optional ReCaptcha v3 token for request authentication
 * @returns Promise resolving to the created ticket information
 * @throws Error if the API request fails or returns invalid data
 */
export async function createTicket({
  endpoint,
  ticketData,
  recaptcha_token,
}: {
  endpoint: string;
  recaptcha_token?: string;
  ticketData: CreateTicketParams;
}): Promise<TicketResponse> {
  try {
    const ticketUrl = new URL('/api/v4/task_management/public/tasks', endpoint).toString();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (recaptcha_token) {
      headers['X-Recaptcha-Token'] = recaptcha_token;
    }

    const { data } = await axios.post<CreateTicketResponse>(ticketUrl, {
      ...ticketData,
      source: 'ai_chat'
    }, { headers });

    const ticket = data.ticket;
    
    if (!ticket || !ticket.id || !ticket.ticket_number) {
      throw new Error('Invalid response format from ticket API');
    }

    return ticket;
  } catch (e) {
    if (isAxiosError(e) && e.response?.data?.errors) {
      const errors = e.response.data.errors;
      throw new Error(`Ticket creation failed: ${Array.isArray(errors) ? errors.join(', ') : errors}`);
    }

    throw new Error(`Ticket creation failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
  }
}
